import { Badge, BadgeLevel, BadgeShape, BadgeSize } from '@vizit/atoms';
import { Skeleton, Text, TextColor, TextSize, TextWeight, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { BENCHMARK_MIN_IMAGES } from '@vizit/constants';
import { AlertCircleIcon, Image01Icon } from '@vizit/icons';
import { BenchmarkMosaic } from '@vizit/molecules';
import { cva, cx } from '@vizit/theme';
import type * as React from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { type BenchmarkAction, BenchmarkActionsMenu } from '../benchmark-actions-menu';

export interface BenchmarkCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  /**
   * Unique identifier for the benchmark
   */
  id: string;
  /**
   * The benchmark name
   */
  name: string;
  /**
   * The product category name; `null` shows "No category yet"
   */
  categoryName?: string | null;
  /**
   * Number of images in the benchmark
   */
  imageCount: number;
  /**
   * Up to four thumbnail URLs for the mosaic; missing cells render empty
   */
  previewImageUrls?: string[];
  /**
   * Display name of the user who created the benchmark
   */
  creatorName?: string;
  /**
   * Whether the card is a loading placeholder
   */
  isLoading?: boolean;
  /**
   * The benchmark page URL. Makes the name a link that covers the whole card, so opening it in a new tab, copying
   * the address and middle-clicking all work. Without it the card is a plain labelled group.
   */
  href?: string;
  /**
   * Called on a plain click of the card, so the app can navigate without a page load. Modified clicks
   * (new tab, new window) are left to the browser.
   */
  onNavigate?: (id: string) => void;
  /**
   * Called with the action chosen from the "⋯" menu. The menu is hidden when omitted.
   */
  onAction?: (id: string, action: BenchmarkAction) => void;
  /**
   * Menu actions shown but not available yet
   */
  disabledActions?: BenchmarkAction[];
}

const cardClasses = cva({
  base: ['group relative flex w-full flex-col bg-white transition-all ease-in-out', 'border border-secondary'],
  variants: {
    isLoading: {
      true: 'cursor-wait opacity-50',
      false: 'hover:border-gray-dark-900 hover:bg-gray-dark-900',
    },
  },
});

// The name link stretches over the whole card through its ::after, which also draws the focus ring. The chip and
// the actions menu sit above it (z-10), so they stay separate controls instead of being nested in the link.
const stretchedLinkClasses = [
  'outline-none after:absolute after:inset-0 after:content-[""]',
  'focus-visible:after:ring-1 focus-visible:after:ring-gray-utility-700',
];

// Text on the card flips to white while the card inverts to dark on hover.
const invertedTextClasses = 'group-hover:text-white';
const invertedMutedTextClasses = 'group-hover:text-gray-utility-300';

function ImagesNeededChip({ imageCount }: { imageCount: number }) {
  const needed = BENCHMARK_MIN_IMAGES - imageCount;
  const images = needed === 1 ? 'image' : 'images';

  return (
    <TooltipTrigger delay={200}>
      <AriaButton
        aria-label={`Needs ${needed} more ${images}`}
        className="relative z-10 cursor-default outline-none data-[focus-visible]:ring-1 data-[focus-visible]:ring-gray-utility-700"
      >
        <Badge level={BadgeLevel.Warning} shape={BadgeShape.Box} size={BadgeSize.Sm} className="gap-2">
          <AlertCircleIcon size={12} aria-hidden />
          <span className="text-xs">{needed} more</span>
        </Badge>
      </AriaButton>
      <Tooltip className="max-w-[240px]">
        Needs {needed} more {images} to score against. A benchmark needs at least {BENCHMARK_MIN_IMAGES}.
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * A custom benchmark in the Library grid: a 2×2 mosaic of its images, its
 * name, category, image count and creator. Sets under the scoring minimum
 * carry a "N more" chip. Inverts to black on hover.
 */
export const BenchmarkCard = (props: BenchmarkCardProps) => {
  const {
    id,
    name,
    categoryName,
    imageCount,
    previewImageUrls = [],
    creatorName,
    isLoading = false,
    href,
    onNavigate,
    onAction,
    disabledActions,
    className,
    ...other
  } = props;

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isModifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (!onNavigate || isModifiedClick || event.defaultPrevented) {
      return;
    }
    event.preventDefault();
    onNavigate(id);
  };

  const needsMoreImages = imageCount < BENCHMARK_MIN_IMAGES;

  // Loading placeholders are hidden from assistive tech; otherwise the card is a labelled group whose name is the
  // link, so it never poses as a button wrapping other buttons.
  const rootProps = isLoading ? { 'aria-hidden': true } : { role: 'group', 'aria-label': name };

  return (
    <div
      className={cx(cardClasses({ isLoading }), href && !isLoading && 'cursor-pointer', className)}
      {...rootProps}
      {...other}
      data-benchmark-id={id}
    >
      <BenchmarkMosaic name={name} urls={previewImageUrls} isLoading={isLoading} />

      <div className="flex flex-col gap-2 p-6">
        {isLoading ? (
          <>
            <Skeleton variant="square" className="h-10 w-full" />
            <Skeleton variant="square" className="h-8 w-2/3" />
            <Skeleton variant="square" className="h-10 w-1/2" />
          </>
        ) : (
          <>
            <Text
              size={TextSize.Md}
              weight={TextWeight.Semibold}
              color={TextColor.Primary}
              className={cx('truncate', invertedTextClasses)}
              title={name}
            >
              {href ? (
                <a href={href} onClick={handleLinkClick} className={cx(stretchedLinkClasses)}>
                  {name}
                </a>
              ) : (
                name
              )}
            </Text>

            <Text
              size={TextSize.Sm}
              color={categoryName ? TextColor.Tertiary : TextColor.Quaternary}
              className={cx('truncate', invertedMutedTextClasses, !categoryName && 'italic')}
              title={categoryName ?? undefined}
            >
              {categoryName ?? 'No category yet'}
            </Text>

            <div className="flex min-h-16 items-center justify-between gap-4 pt-4">
              <div className="flex min-w-0 items-center gap-4">
                <span className="flex shrink-0 items-center gap-2">
                  <Image01Icon
                    size={16}
                    className={cx('text-gray-utility-400', invertedMutedTextClasses)}
                    aria-hidden
                  />
                  <Text
                    size={TextSize.Sm}
                    weight={TextWeight.Medium}
                    color={TextColor.Tertiary}
                    className={invertedMutedTextClasses}
                  >
                    {imageCount}
                    <span className="sr-only">{imageCount === 1 ? ' image' : ' images'}</span>
                  </Text>
                </span>
                {creatorName && (
                  <span aria-hidden className={cx('text-gray-utility-300', invertedMutedTextClasses)}>
                    ·
                  </span>
                )}
                {creatorName && (
                  <Text
                    size={TextSize.Sm}
                    color={TextColor.Quaternary}
                    className={cx('truncate', invertedMutedTextClasses)}
                    title={creatorName}
                  >
                    {creatorName}
                  </Text>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {needsMoreImages && <ImagesNeededChip imageCount={imageCount} />}
                {onAction && (
                  <BenchmarkActionsMenu
                    benchmarkName={name}
                    onAction={(action) => onAction(id, action)}
                    disabledActions={disabledActions}
                    className={cx(
                      'relative z-10 text-quaternary',
                      invertedTextClasses,
                      'group-hover:hover:bg-gray-dark-800',
                    )}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

BenchmarkCard.displayName = 'BenchmarkCard';
